import {Controller} from '@hotwired/stimulus'

const STATE_KEY = 'gfMeetingFrameSrc'

// Turbo leaves these to the browser (new tab/window, download), so the frame doesn't navigate.
function isModifiedClick(event) {
  return event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey
}

// TEMPORARY WORKAROUND — DELETE WITH GF-2957 (cleanup: GF-4086).
// It exists because React Router owns /orgnav/ history, so data-turbo-action="advance"
// can't work on this frame. Once GF-2957 retires the outer router, delete this
// controller and put data-turbo-action="advance" on the frame instead.
//
// Gives browser Back/Forward a history entry for each link navigation inside a
// custom meeting's content frame, e.g. opening a project in Project Updates (GF-3578).
//
// The frame has no data-turbo-action, so those navigations add no history entry and
// Back skipped straight past the meeting step. It can't use "advance": that would put
// the project's URL in the address bar, and the meeting is an /orgnav/ page whose
// history React Router owns (historySync leaves it alone), so a Back to the meeting
// URL would restore nothing. Instead each entry keeps the meeting URL and carries the
// frame's src in history.state; popstate restores the frame from it. Entries without
// that key are left alone.
export default class extends Controller {
  connect() {
    this.linkClicked = false
    this.onClick = this.onClick.bind(this)
    this.onFrameLoad = this.onFrameLoad.bind(this)
    this.onPopstate = this.onPopstate.bind(this)
    this.element.addEventListener('click', this.onClick)
    this.element.addEventListener('turbo:frame-load', this.onFrameLoad)
    window.addEventListener('popstate', this.onPopstate)
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClick)
    this.element.removeEventListener('turbo:frame-load', this.onFrameLoad)
    window.removeEventListener('popstate', this.onPopstate)
  }

  // The entry is written on the click, not when the frame loads: Turbo fires frame-load
  // a few animation frames after the new content shows, and a Back in that gap would
  // otherwise leave the meeting.
  onClick(event) {
    const link = event.target.closest('a[href]')
    if (event.defaultPrevented || isModifiedClick(event))
      return
    if (!this.navigatesThisFrame(link) || link.href === this.element.src)
      return
    this.linkClicked = true
    // Tag the entry we're leaving so Back can return the frame to what it shows now.
    window.history.replaceState({...window.history.state, [STATE_KEY]: this.element.src}, '')
    // Keep React Router's key/state, but not Turbo's: this entry is not a Turbo
    // restoration point, and a copied restorationIdentifier would make Turbo (with
    // Drive on) run a restore visit when Back lands here.
    const {turbo, ...state} = window.history.state || {}
    window.history.pushState({...state, [STATE_KEY]: link.href}, '', window.location.href)
  }

  // Record where the frame actually landed, in case the link redirected.
  onFrameLoad(event) {
    if (event.target !== this.element || !this.linkClicked)
      return
    this.linkClicked = false
    if (window.history.state?.[STATE_KEY] !== this.element.src)
      window.history.replaceState({...window.history.state, [STATE_KEY]: this.element.src}, '')
  }

  onPopstate(event) {
    const src = event.state?.[STATE_KEY]
    if (!src || src === this.element.src)
      return
    this.linkClicked = false
    this.element.src = src
  }

  // A link navigates this frame when it targets it by id, or has no target and this is
  // its nearest frame (links inside nested per-record frames navigate those instead).
  // Turbo leaves cross-origin links to the browser, so they never load here.
  navigatesThisFrame(link) {
    if (!link || link.dataset.turbo === 'false' || link.target || link.origin !== window.location.origin)
      return false
    const target = link.dataset.turboFrame
    return target ? target === this.element.id : link.closest('turbo-frame') === this.element
  }
}
